import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import SmartToyIcon from '@mui/icons-material/SmartToy'
import { alpha, Box, Chip, Tooltip, Typography, useTheme } from '@mui/material'
import React from 'react'
import { CatalogItem, classifyItem, getPhaseShortLabel, Phase } from './types'

export const SingleItemCard: React.FC<{
  item: CatalogItem
  activePhaseItemIds: Set<string>
  getItemPhases: (productId: string) => Set<Phase>
  removeFromAll: (productId: string) => void
  addItem: (item: CatalogItem) => void
  handleOpenMultiMonth: (event: React.MouseEvent<HTMLElement>, item: CatalogItem) => void
  effectiveRate: number
  slColor: string
  SlIcon: any
  groupLabel: string
  isSetupPhase: boolean
  activePhase: Phase
  recommendedSkus: Map<string, string>
}> = ({
  item,
  activePhaseItemIds,
  getItemPhases,
  removeFromAll,
  addItem,
  handleOpenMultiMonth,
  effectiveRate,
  slColor,
  SlIcon,
  groupLabel,
  isSetupPhase,
  activePhase,
  recommendedSkus,
}) => {
  const theme = useTheme()
  const aiAccent = theme.palette.text.ai_accent ?? '#A855F7'

  const isAdded = activePhaseItemIds.has(item.products_uuid)
  const cat = classifyItem(item)
  const itemPhases = getItemPhases(item.products_uuid)
  const dollarValue = (item.points || 0) * effectiveRate
  const aiReason = item.sku ? recommendedSkus.get(item.sku) : undefined
  const isRecommended = Boolean(aiReason)

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: isAdded
          ? alpha(theme.palette.text.complete, 0.4)
          : isRecommended
          ? alpha(aiAccent, 0.4)
          : 'divider',
        bgcolor: isAdded
          ? alpha(theme.palette.text.complete, 0.04)
          : isRecommended
          ? alpha(aiAccent, 0.04)
          : 'background.paper',
        transition: 'all 0.15s',
        overflow: 'hidden',
        '&:hover': {
          borderColor: isAdded
            ? alpha(theme.palette.text.complete, 0.5)
            : isRecommended
            ? alpha(aiAccent, 0.6)
            : alpha(theme.palette.text.primary, 0.15),
          transform: 'translateY(-1px)',
          boxShadow: isRecommended ? `0 2px 12px ${alpha(aiAccent, 0.2)}` : '0 2px 8px rgba(0,0,0,0.15)',
        },
      }}
    >
      {/* Card header */}
      <Box sx={{ p: 1.5, pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <Chip
            label={cat === 'strategic' ? 'Strategic' : 'Executable'}
            size="small"
            sx={{
              height: 20,
              fontSize: 10,
              fontWeight: 600,
              bgcolor:
                cat === 'strategic' ? alpha(theme.palette.text.link, 0.12) : alpha(theme.palette.text.highlight, 0.12),
              color: cat === 'strategic' ? theme.palette.text.link : theme.palette.text.highlight,
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
          {SlIcon && (
            <Chip
              icon={<SlIcon sx={{ fontSize: 12, color: slColor }} />}
              label={groupLabel}
              size="small"
              sx={{
                height: 20,
                fontSize: 10,
                bgcolor: `${slColor}18`,
                color: slColor,
                '& .MuiChip-label': { px: 0.5 },
                '& .MuiChip-icon': { ml: 0.25 },
              }}
            />
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto' }}>
            {isRecommended && (
              <Tooltip title={aiReason || 'AI Recommended'} arrow>
                <AutoAwesomeIcon sx={{ fontSize: 14, color: aiAccent }} />
              </Tooltip>
            )}
            {item.ai_enhanced && (
              <Tooltip title="AI Enhanced Service">
                <SmartToyIcon sx={{ fontSize: 14, color: 'primary.main' }} />
              </Tooltip>
            )}
          </Box>
        </Box>

        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            lineHeight: 1.3,
            mb: 0.25,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {item.title}
        </Typography>

        {item.sku && (
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
            {item.sku}
          </Typography>
        )}
      </Box>

      {/* Points + price */}
      <Box sx={{ px: 1.5, pb: 1, display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, color: slColor }}>
          {item.points || 0} pts
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          ${dollarValue.toLocaleString()}
        </Typography>
      </Box>

      {/* Subcategory / delivery role */}
      {(item.subcategory || item.delivery_role) && (
        <Box sx={{ px: 1.5, pb: 1 }}>
          <Chip
            label={item.subcategory || item.delivery_role}
            size="small"
            sx={{
              height: 18,
              fontSize: 10,
              bgcolor: `${slColor}15`,
              color: slColor,
              '& .MuiChip-label': { px: 0.75 },
            }}
          />
        </Box>
      )}

      {/* Action buttons */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderTop: '1px solid',
          borderColor: 'divider',
          mt: 'auto',
        }}
      >
        <Box sx={{ display: 'flex' }}>
          <Tooltip
            title={
              isAdded ? `Already in ${getPhaseShortLabel(activePhase)}` : `Add to ${getPhaseShortLabel(activePhase)}`
            }
          >
            <Box
              component="button"
              onClick={() => !isAdded && addItem(item)}
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                py: 0.75,
                border: 'none',
                bgcolor: 'transparent',
                color: isAdded ? theme.palette.text.complete : 'text.secondary',
                cursor: isAdded ? 'default' : 'pointer',
                fontSize: 12,
                fontWeight: 600,
                fontFamily: 'inherit',
                transition: 'all 0.15s',
                '&:hover': !isAdded
                  ? { bgcolor: alpha(theme.palette.text.complete, 0.08), color: theme.palette.text.complete }
                  : {},
              }}
            >
              {isAdded ? <CheckCircleIcon sx={{ fontSize: 15 }} /> : <AddCircleOutlineIcon sx={{ fontSize: 15 }} />}+
              Add to {getPhaseShortLabel(activePhase)}
            </Box>
          </Tooltip>

          {!isSetupPhase && (
            <Tooltip title="Add to multiple months">
              <Box
                component="button"
                onClick={e => handleOpenMultiMonth(e, item)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: 1,
                  py: 0.75,
                  border: 'none',
                  borderLeft: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'transparent',
                  color: 'text.secondary',
                  cursor: 'pointer',
                  fontSize: 11,
                  fontFamily: 'inherit',
                  transition: 'all 0.15s',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.text.ai_accent_secondary, 0.08),
                    color: theme.palette.text.ai_accent_secondary,
                  },
                }}
              >
                <CalendarMonthIcon sx={{ fontSize: 15 }} />
              </Box>
            </Tooltip>
          )}
        </Box>

        {itemPhases.size > 0 && (
          <Box
            component="button"
            onClick={() => removeFromAll(item.products_uuid)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.5,
              py: 0.5,
              border: 'none',
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: 'transparent',
              color: 'text.disabled',
              cursor: 'pointer',
              fontSize: 11,
              fontFamily: 'inherit',
              transition: 'all 0.15s',
              '&:hover': { bgcolor: alpha(theme.palette.text.error, 0.06), color: theme.palette.text.error },
            }}
          >
            <RemoveCircleOutlineIcon sx={{ fontSize: 13 }} />
            Remove from all ({itemPhases.size})
          </Box>
        )}
      </Box>
    </Box>
  )
}
